import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { Sparkles, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

export default function LoginPage() {
  const { login, loginAsDemo, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);

  const from = location.state?.from || "/account";

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      await login(email, password);
      toast.success("Welcome back to Scentodore.");
      navigate(from);
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed. Please try again.");
    }
  };

  const demo = async () => {
    try {
      await loginAsDemo();
      toast.success("Logged in as Demo Guest.");
      navigate(from);
    } catch (err) {
      toast.error("Could not start demo session.");
    }
  };

  return (
    <main data-testid="login-page" className="relative min-h-[70vh] sd-dark sd-dark-noise">
      <div className="absolute -top-32 -left-32 w-[420px] h-[420px] sd-amber-glow-dark rounded-full pointer-events-none" />
      <div className="absolute -bottom-24 -right-24 w-[420px] h-[420px] sd-amber-glow-dark rounded-full pointer-events-none opacity-70" />
      <div className="relative max-w-md mx-auto px-5 sm:px-6 py-14 sm:py-24">
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#D4B278]">
            <Sparkles className="h-3 w-3" /> Scentodore Society
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl mt-3 text-[#FAF8F5]">Welcome back.</h1>
          <p className="mt-3 text-sm text-[#FAF8F5]/70">Sign in to view your olfactory profile, wardrobe, and loyalty tier.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label className="text-xs text-[#FAF8F5]/70 uppercase tracking-[0.14em]">Email</Label>
              <Input
                type="email" required autoComplete="email"
                data-testid="login-email"
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-2 bg-white/5 border-white/10 text-[#FAF8F5] placeholder:text-white/40 rounded-xl"
              />
            </div>
            <div>
              <Label className="text-xs text-[#FAF8F5]/70 uppercase tracking-[0.14em]">Password</Label>
              <Input
                type="password" required autoComplete="current-password"
                data-testid="login-password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-2 bg-white/5 border-white/10 text-[#FAF8F5] placeholder:text-white/40 rounded-xl"
              />
            </div>
            {error && <div data-testid="login-error" className="text-xs text-red-300">{error}</div>}
            <Button
              type="submit" data-testid="login-submit"
              disabled={loading}
              className="w-full bg-[#C5A059] text-[#0B0A08] hover:bg-[#D4B278] rounded-xl py-6 text-sm uppercase tracking-[0.16em] shadow-[0_10px_30px_rgba(197,160,89,0.35)]"
            >
              {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Signing in</> : "Sign In"}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-3 text-[10px] uppercase tracking-[0.16em] text-[#FAF8F5]/40">
            <div className="flex-1 h-px bg-white/10" /> or <div className="flex-1 h-px bg-white/10" />
          </div>

          <Button
            variant="outline" data-testid="login-demo-button"
            onClick={demo} disabled={loading}
            className="w-full border-[#D4B278]/50 text-[#FAF8F5] hover:bg-[#D4B278]/10 hover:border-[#D4B278] rounded-xl py-5 text-sm uppercase tracking-[0.14em] bg-transparent"
          >
            Continue as Demo Guest <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
          <div className="mt-2 text-[10px] text-[#FAF8F5]/40 text-center tracking-wide">Instant preview — Golden Amber tier with sample history</div>

          <div className="mt-8 text-center text-sm text-[#FAF8F5]/70">
            New to Scentodore? <Link to="/register" data-testid="login-register-link" className="text-[#D4B278] hover:underline">Create an account</Link>
          </div>
        </motion.div>
      </div>
    </main>
  );
}
